// Synthesising BOTH `routes.list` AND `fullPageRoute` is intentional: the back
// button in `CRMObjectFullPageView` defaults to `config.routes.list`. Without
// synthesising `routes.list`, the back button would route to FALLBACK_CONFIG's
// generic `/:orgId/crm`, losing entity context.
//
// Do NOT set `aiSummarySource` — leaving it undefined yields the null-AISummaryPanel
// behaviour required by the acceptance criteria.

import { useCallback, useMemo } from 'react';

import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '@/lib/crm/core/SchemaClient';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import {
  associationMatchesSource,
  buildEntityKindMap,
  getEntityKinds,
} from '@/lib/crm/utils/associationMatching';
import { toKebabSlug } from '@/lib/crm/utils/entityTypeUtils';

import {
  ENTITY_TYPE_REGISTRY,
  type EntityTypeConfig,
  FALLBACK_CONFIG,
  getEntityTypeConfig,
  type HighlightDefinition,
} from './entityTypeRegistry';

const MAX_AUTO_HIGHLIGHTS = 6;

const EMPTY_OBJECT_TYPES: CrmObjectTypeSchema[] = [];
const EMPTY_ASSOCIATION_TYPES: AssociationType[] = [];

export function buildCustomEntityHighlights(
  entityTypeName: string,
  objectTypes: CrmObjectTypeSchema[],
  associationTypes: AssociationType[]
): HighlightDefinition[] {
  const objectTypeByName = new Map(
    objectTypes.map(t => [t.entity_type_name, t])
  );

  // Build entity_kind lookup so we can match kind-based associations. For
  // custom (org_*) entity types this is usually a no-op (they declare
  // type-based associations), but doing kind-aware matching here keeps this
  // call site consistent with the rest of the codebase and future-proofs
  // against custom kind-based associations.
  const entityKindMap = buildEntityKindMap(objectTypes);
  const sourceKinds = getEntityKinds(entityTypeName, entityKindMap);

  // Spec mentions value_write_access; the readability gate on this schema is value_read_access.
  // `*_timeline` source/target entity types are not valid identifiers for user-facing
  // entity flows (they alias the underlying entity type) and must be excluded so we
  // don't synthesise broken highlight cards. Matches the pattern used by useObjectTypes,
  // useStages, registry.helpers, MixedActionTable, LinkActionCard.
  const candidates = associationTypes.filter(
    at =>
      associationMatchesSource(at, entityTypeName, sourceKinds) &&
      !at.source_entity_type_name.endsWith('_timeline') &&
      !at.target_entity_type_name.endsWith('_timeline') &&
      (at.cardinality === 'one_to_many' || at.cardinality === 'many_to_one') &&
      at.value_read_access === 'all'
  );

  candidates.sort((a, b) => {
    if (a.cardinality !== b.cardinality) {
      return a.cardinality === 'many_to_one' ? -1 : 1;
    }
    return a.association_type_name.localeCompare(b.association_type_name);
  });

  const out: HighlightDefinition[] = [];
  for (const at of candidates) {
    if (out.length >= MAX_AUTO_HIGHLIGHTS) {
      break;
    }
    const target = objectTypeByName.get(at.target_entity_type_name);
    if (target === undefined) {
      continue;
    }
    if (at.cardinality === 'many_to_one') {
      out.push({
        kind: 'association',
        label: target.label,
        associationType: at.association_type_name,
      });
    } else {
      out.push({
        kind: 'association_count',
        label: target.label_plural,
        associationType: at.association_type_name,
      });
    }
  }

  return out;
}

export function buildCustomEntityConfig(
  entityTypeName: string,
  objectTypeMatch: CrmObjectTypeSchema | undefined,
  objectTypes: CrmObjectTypeSchema[],
  associationTypes: AssociationType[]
): EntityTypeConfig {
  const slug = toKebabSlug(entityTypeName);
  return {
    ...FALLBACK_CONFIG,
    displayName: objectTypeMatch?.label ?? FALLBACK_CONFIG.displayName,
    pluralDisplayName:
      objectTypeMatch?.label_plural ?? FALLBACK_CONFIG.pluralDisplayName,
    routes: {
      list: `/:orgId/crm/${slug}`,
      detail: `/:orgId/crm/${slug}/:id`,
    },
    fullPageRoute: `/:orgId/crm/${slug}/:recordId`,
    highlights: buildCustomEntityHighlights(
      entityTypeName,
      objectTypes,
      associationTypes
    ),
  };
}

export interface UseBuildCustomEntityConfigResult {
  config: EntityTypeConfig;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Derive an EntityTypeConfig for any entity type at runtime.
 *
 * Registered entity types return the static registry config synchronously
 * (no schema fetch). Unregistered custom (org_*) types fetch schema via
 * the shared React Query cache and derive highlights from associations.
 */
export function useBuildCustomEntityConfig(
  orgId: number | undefined,
  entityTypeName: string | undefined
): UseBuildCustomEntityConfigResult {
  const isRegistered =
    !!entityTypeName && entityTypeName in ENTITY_TYPE_REGISTRY;

  // Gate the schema fetches: registered (and undefined) entity types never read
  // from `objectTypesQuery`/`associationTypes`, so skip the queries entirely.
  // Passing `undefined` flips both hooks' `enabled` flag off (no network request).
  const schemaOrgId = entityTypeName && !isRegistered ? orgId : undefined;

  const objectTypesQuery = useObjectTypes(schemaOrgId);
  const {
    associationTypes,
    isLoading: assocLoading,
    error: assocError,
    refetch: refetchAssoc,
  } = useAssociationTypes(schemaOrgId);

  const allObjectTypes =
    objectTypesQuery.data?.allObjectTypes ?? EMPTY_OBJECT_TYPES;
  const associations =
    associationTypes.length === 0 ? EMPTY_ASSOCIATION_TYPES : associationTypes;

  const config = useMemo<EntityTypeConfig>(() => {
    if (!entityTypeName) {
      return FALLBACK_CONFIG;
    }
    // `*_timeline` identifiers alias the underlying entity type's event log and
    // are not valid entity/object identifiers for user-facing entity flows.
    // Synthesising a full-page config for them would produce broken routes and
    // highlight cards. Fall back to the generic config instead.
    if (entityTypeName.endsWith('_timeline')) {
      return FALLBACK_CONFIG;
    }
    if (isRegistered) {
      return getEntityTypeConfig(entityTypeName);
    }
    const match = allObjectTypes.find(
      t => t.entity_type_name === entityTypeName
    );
    return buildCustomEntityConfig(
      entityTypeName,
      match,
      allObjectTypes,
      associations
    );
  }, [entityTypeName, isRegistered, allObjectTypes, associations]);

  const isLoading =
    !!entityTypeName &&
    !isRegistered &&
    (objectTypesQuery.isLoading || assocLoading);

  const { error: queryError } = objectTypesQuery;
  let error: Error | null = null;
  if (entityTypeName && !isRegistered) {
    if (queryError instanceof Error) {
      error = queryError;
    } else if (assocError instanceof Error) {
      error = assocError;
    }
  }

  const { refetch: refetchObjectTypes } = objectTypesQuery;
  const refetch = useCallback(() => {
    void refetchObjectTypes();
    void refetchAssoc();
  }, [refetchObjectTypes, refetchAssoc]);

  return { config, isLoading, error, refetch };
}
